import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWhatsApp } from '../../context/WhatsAppContext';
import WhatsAppService from '../../services/whatsapp.service';
import { getUserPhoneNumber } from './AgentMessage';

interface CompanyContactCardProps {
  companyName: string;
  phoneNumber: string;
  profileImage?: string;
  onWhatsAppSent?: () => void; // Callback opcional para cuando se envía el mensaje de WhatsApp
}

const CompanyContactCard: React.FC<CompanyContactCardProps> = ({ companyName, phoneNumber, profileImage, onWhatsAppSent }) => {
  const defaultImage = 'https://via.placeholder.com/50';
  const { userInfo } = useAuth();
  const { whatsappData } = useWhatsApp();
  const messageSentRef = useRef(false); // Referencia para controlar si el mensaje ya se ha enviado

  // Función para formatear el número de teléfono como enlace de WhatsApp
  const formatWhatsAppLink = (phone: string) => {
    // Eliminar caracteres no numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    return `https://wa.me/${cleanPhone}`;
  };

  // Efecto para enviar la confirmación por WhatsApp cuando se muestra la tarjeta
  useEffect(() => {
    const sendWhatsAppConfirmation = async () => {
      try {
        // Verificar si el mensaje ya se ha enviado
        if (messageSentRef.current) {
          return;
        }

        if (!userInfo || !whatsappData.ServiceName || !whatsappData.CityName || !whatsappData.DateTime) {
          return;
        }

        // Formatear el número de teléfono con el prefijo internacional
        const formattedPhone = WhatsAppService.formatPhoneNumber(phoneNumber);

        // Marcar el mensaje como enviado antes de enviarlo
        messageSentRef.current = true;

        // Obtener el número de teléfono del usuario capturado del chat
        const userPhoneNumber = getUserPhoneNumber();

        // Enviar la confirmación por WhatsApp
        await WhatsAppService.sendConfirmation({
          To: formattedPhone,
          CompanyName: companyName,
          ServiceName: whatsappData.ServiceName || '',
          UserRequestName: `${userInfo.name} ${userInfo.lastname}`,
          DateTime: whatsappData.DateTime || '', // El servicio se encarga de formatear la fecha
          CityName: whatsappData.CityName || '',
          UserRequestEmail: userInfo.email,
          userRequestPhone: userPhoneNumber || undefined // Número de teléfono del usuario que solicita el servicio
        });

        // Llamar al callback si existe
        if (onWhatsAppSent) {
          onWhatsAppSent();
        }
      } catch (error) {
        // En caso de error, resetear la bandera para permitir un reintento
        messageSentRef.current = false;
      }
    };

    // Enviar la confirmación por WhatsApp solo si no se ha enviado antes
    if (!messageSentRef.current) {
      sendWhatsAppConfirmation();
    }
  }, [phoneNumber, userInfo, whatsappData, onWhatsAppSent, companyName]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-2 md:p-4 my-3 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <img
            src={profileImage || defaultImage}
            alt={`${companyName} logo`}
            className=" w-8 h-8 md:w-12 md:h-12 rounded-full object-cover border-2 border-xervix-primary"
          />
        </div>
        <div className="ml-3 md:ml-4 flex-1">
          <h3 className="text-sm md:text-lg font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
            {companyName}
          </h3>
          <div className="mt-1 flex items-center">
            <a
              href={formatWhatsAppLink(phoneNumber)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-green-600 hover:text-green-800"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="md:h-5 md:w-5 h-1 w-1 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className="text-sm">{phoneNumber}</span>
            </a>
          </div>
        </div>
        <div className="ml-2">
          <a
            href={formatWhatsAppLink(phoneNumber)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22.5c-5.799 0-10.5-4.701-10.5-10.5S6.201 1.5 12 1.5 22.5 6.201 22.5 12 17.799 22.5 12 22.5z" />
            </svg>
            WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default CompanyContactCard;
